import { Grid, Paper, Button, Tooltip } from "@material-ui/core";
import CustomInput from "components/CustomInput";
import { useGetBusinessUnitsQuery, useGetClientsWithConfigsQuery } from "features/clients/clientConfigSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { setErrors } from "features/loads/loadsStateSlice";
import { selectIsClient, selectUserClientId } from "features/user/userSlice";
import VinDecode from "features/vehicles/VinDecode/VinDecodePopup";
import VinMatching from "features/vehicles/components/VinMatching/VinMatching";
import { useGetFuelTypesQuery, useGetVehicleTypesQuery } from "features/vehicles/vehicleSlice";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Validator from "services/validator";
import { arrayToObject } from "utils/object-util";
import GroupAssetFormStyles from "./GroupAssetFormStyles";
import SearchForAsset from "../SearchForAsset/SearchForAsset";
import { searchByFieldTypes } from "features/vehicles/consts";
import { vehicle_detail_extended_type, weightClassValues } from "features/vehicles/VehicleDetailExtended/VehicleDetailExtendedTypes";
import DeleteIcon from '@mui/icons-material/Delete';

const useStyles = GroupAssetFormStyles;

const emptyAsset = () => ({
  vin: "",
  assetId: "",
  year: "",
  make: "",
  model: "",
  assetTypeId: null,
  fuelType: null,
  vehicleId: null,
  id: 0,
  weightClass: null,
});

const GroupAssetForm = ({
  loadGroup,
  setLoadGroup,
  clientBUs,
  setClientBUs,
  disableAssetDetails,
  legIndex,
  resetSearch,
}) => {
  let { data: vehicleTypes, isLoading: isLoadingVehicleTypes } = useGetVehicleTypesQuery();
  let { data: clientsWithConfigs, isFetching: isLoadingClientsWithConfigs } = useGetClientsWithConfigsQuery(null);
  let { data: fuelTypes } = useGetFuelTypesQuery();
  let { data: businessUnits } = useGetBusinessUnitsQuery();
  let { data: clients } = useGetClientsQuery(null);

  vehicleTypes = vehicleTypes || [];
  clientsWithConfigs = clientsWithConfigs || [];
  clients = clients || [];

  const isClient = useSelector(selectIsClient);
  const loggedInUserClientId = useSelector(selectUserClientId);

  const { showMandatoryError, errors } = useSelector((state) => state.loadState);
  const dispatch = useDispatch();
  const classes = useStyles();

  // Per-vehicle decode state
  const [decodedByIndex, setDecodedByIndex] = useState({});
  const [colorDecodedByIndex, setColorDecodedByIndex] = useState({});
  const [openDecodedByIndex, setOpenDecodedByIndex] = useState({});

  const filteredClientConfigIds = useMemo(
    () => clientsWithConfigs?.filter((c) => c?.config?.transportConfig?.useTransport === true).map((c) => c.id) || [],
    [clientsWithConfigs]
  );

  const clientAccounts = useMemo(() => {
    const accounts = _.flatten((clients || []).map((c) => c.clientAccounts)) || [];
    return accounts.filter((a) => filteredClientConfigIds.includes(a.clientId));
  }, [clients, filteredClientConfigIds]);

  const clientAccountsCount = clientAccounts?.length || 0;
  const clientLookup = useMemo(() => arrayToObject(clientAccounts, "id"), [clientAccounts]);

  const isLoading = () => isLoadingVehicleTypes || isLoadingClientsWithConfigs;
  const showClientDropdown = () => clientAccountsCount > 1;

  // Ensure current leg has assets array, but DO NOT loop:
  // Only set if missing/empty.
  useEffect(() => {
    setLoadGroup((prev) => {
      const loads = [...(prev?.loads || [])];
      const leg = loads?.[legIndex];
      if (!leg) return prev;

      if (Array.isArray(leg.assets) && leg.assets.length > 0) return prev;

      loads[legIndex] = {
        ...leg,
        assets: [emptyAsset()],
      };
      return { ...prev, loads };
    });
  }, [legIndex, setLoadGroup]);



  // Business units (avoid infinite loops by removing unused deps)
  useEffect(() => {
    if (!businessUnits?.length) return;

    const clientId = loadGroup?.clientId ?? loggedInUserClientId?.[0];
    const next = businessUnits.filter((b) => b.clientId == clientId);

    setClientBUs((prev) => {
      const prevIds = (prev || []).map((x) => x.id).join(",");
      const nextIds = next.map((x) => x.id).join(",");
      if (prevIds === nextIds) return prev;
      return next;
    });
  }, [loadGroup?.clientId, businessUnits, loggedInUserClientId, setClientBUs]);

  const assets = useMemo(() => {
    const a = loadGroup?.loads?.[legIndex]?.assets;
    return Array.isArray(a) && a.length ? a : [emptyAsset()];
  }, [loadGroup?.loads, legIndex]);

  const getErrorMessagesForField = {
    vin: (val) => {
      if (!Validator.validateVin(val)) return "Invalid VIN";
      return false;
    },
    year: (val) => {
      if (!(val && val > 1980 && val.length <= 4)) return "Invalid year";
      return false;
    },
  };

  const clearErrorForFields = (keys) => {
    const newErrors = { ...errors };
    keys.forEach((k) => {
      if (newErrors[k]) delete newErrors[k];
    });
    dispatch(setErrors(newErrors));
  };

  const updateAsset = (assetIndex, values) => {
    setLoadGroup((prev) => {
      const loads = [...(prev?.loads || [])];
      const leg = { ...(loads?.[legIndex] || {}) };

      const nextAssets = (leg.assets || [emptyAsset()]).map((a) => ({ ...a }));
      nextAssets[assetIndex] = { ...(nextAssets[assetIndex] || emptyAsset()), ...values };

      leg.assets = nextAssets;
      loads[legIndex] = leg;
      return { ...prev, loads };
    });
  };

  const onGroupChange = (key, val) => {
    setLoadGroup((prev) => ({ ...prev, [key]: val }));
  };

  const onAssetChange = (assetIndex, key, val) => {
    const errorMessage = getErrorMessagesForField[key]?.(val);
    if (errorMessage) dispatch(setErrors({ ...errors, [key]: errorMessage }));
    else clearErrorForFields([key]);

    updateAsset(assetIndex, { [key]: val });
  };

  const handleSearchFieldChange = (assetIndex, field, val) => {
    if (typeof val === "string") {
      onAssetChange(assetIndex, field, val);
      if (val === "") {
        updateAsset(assetIndex, emptyAsset());
      }
      return;
    }

    const vehicle = val;
    const value = field === searchByFieldTypes.VIN ? vehicle?.descriptor : vehicle?.assetId;

    onAssetChange(assetIndex, field, value);

    updateAsset(assetIndex, {
      id: vehicle?.id ?? assets?.[assetIndex]?.vehicleId,
      vin: vehicle?.descriptor ?? assets?.[assetIndex]?.vin,
      assetId: vehicle?.assetId ?? assets?.[assetIndex]?.assetId,
      year: vehicle?.year ?? assets?.[assetIndex]?.year,
      make: vehicle?.make ?? assets?.[assetIndex]?.make,
      model: vehicle?.model ?? assets?.[assetIndex]?.model,
      assetTypeId: vehicle?.vehicleTypeId ?? assets?.[assetIndex]?.assetTypeId,
      fuelType: vehicle?.fuelType ?? vehicle?.fuelType ?? assets?.[assetIndex]?.fuelType,
      vehicleId: vehicle?.id ?? assets?.[assetIndex]?.vehicleId,
      weightClass:
        parseInt(
          vehicle?.extendedDetails?.find(
            (d) => d.vehicleDetailExtendedTypeId === vehicle_detail_extended_type.VehicleWeightClass
          )?.value
        ) || assets?.[assetIndex]?.weightClass || null,
    });
  };

  const populateDecoded = (assetIndex) => {
    setOpenDecodedByIndex((p) => ({ ...p, [assetIndex]: false }));

    const decodedData = decodedByIndex?.[assetIndex];
    if (!decodedData) return;

    setColorDecodedByIndex((p) => ({ ...p, [assetIndex]: true }));

    updateAsset(assetIndex, {
      year: decodedData?.year,
      make: decodedData?.make,
      model: decodedData?.model,
      weightClass: decodedData?.weightClass,
    });

    setTimeout(() => {
      setColorDecodedByIndex((p) => ({ ...p, [assetIndex]: false }));
    }, 5000);
  };

  const addVehicle = () => {
    setLoadGroup((prev) => {
      const loads = [...(prev?.loads || [])];
      const leg = { ...(loads?.[legIndex] || {}) };
      const nextAssets = Array.isArray(leg.assets) && leg.assets.length ? [...leg.assets] : [emptyAsset()];

      nextAssets.push(emptyAsset());
      leg.assets = nextAssets;
      loads[legIndex] = leg;
      return { ...prev, loads };
    });
  };

  const removeVehicle = (assetIndex) => {
    if (assetIndex === 0) return;

    setLoadGroup((prev) => {
      const loads = [...(prev?.loads || [])];
      const leg = { ...(loads?.[legIndex] || {}) };
      const nextAssets = [...(leg.assets || [])];

      nextAssets.splice(assetIndex, 1);
      leg.assets = nextAssets.length ? nextAssets : [emptyAsset()];
      loads[legIndex] = leg;
      return { ...prev, loads };
    });
  };

  return (
    <Paper className={classes.formContainer}>
      <form id="createLoadForm" className={`${classes.form} ${isLoading() && classes.disabledForm}`}>
        <Grid direction="row" container>
          <Grid item xs={12}>
            {!loadGroup.id && loadGroup?.loads?.length > 1 && (
              <div className={classes.input}>
                <CustomInput
                  elementType="text"
                  value={loadGroup?.groupName}
                  label="Group Name"
                  onChange={(val) => onGroupChange("groupName", val)}
                />
              </div>
            )}

            <Grid container direction="row">
              <Grid item xs={12}>
                <h4> Select a client </h4>
              </Grid>
              {!loadGroup.id && showClientDropdown() && (
                <Grid item xs={6}>
                  <div className={classes.input}>
                    <CustomInput
                      id="clientAccountId"
                      label="Client"
                      value={loadGroup?.clientAccountId}
                      elementType="dropdown"
                      onChange={(val) => {
                        setLoadGroup((prev) => ({
                          ...prev,
                          clientId: clientLookup?.[val]?.clientId,
                          clientAccountId: +val,
                        }));
                      }}
                      values={
                        disableAssetDetails
                          ? [
                            {
                              id: loadGroup?.clientAccountId,
                              name: clientAccounts?.find((c) => c.id === loadGroup?.clientAccountId)?.name,
                            },
                          ]
                          : clientAccounts
                      }
                      showEmpty
                      touched
                      disabled={disableAssetDetails && !!loadGroup?.clientAccountId}
                    />
                  </div>
                </Grid>
              )}

              {!!clientBUs?.length && (
                <Grid item xs={6}>
                  <div className={classes.input}>
                    <CustomInput
                      id="businessUnit"
                      label="Business Unit"
                      value={loadGroup?.businessUnit}
                      elementType="dropdown"
                      onChange={(val) => onGroupChange("businessUnit", +val)}
                      values={clientBUs}
                      valid={!(showMandatoryError && !loadGroup?.businessUnit)}
                      showEmpty
                      required
                      touched
                    />
                  </div>
                </Grid>
              )}
            </Grid>

            <Grid container direction="row" style={{ marginTop: 8 }} >
              <Grid item xs={12}>
                {assets.map((asset, assetIndex) => {
                  const decodedData = decodedByIndex?.[assetIndex] || {};
                  const colorDecoded = !!colorDecodedByIndex?.[assetIndex];
                  const openDecoded = !!openDecodedByIndex?.[assetIndex];

                  return (
                    <Paper key={assetIndex} style={{ padding: 16, marginBottom: 5, border: "1px solid #ccc" }}>
                      <Grid container alignItems="center" justifyContent="space-between" style={{ padding: "5px 10px" }}>
                        <Grid item>
                          <h4 style={{ margin: 0 }}>
                            Vehicle {assetIndex + 1}
                            {asset?.vin ? ` — VIN: ${asset.vin}` : ""}
                          </h4>
                        </Grid>
                        <Grid item>
                          {assetIndex > 0 && (
                            <Button color="secondary" variant="text" onClick={() => removeVehicle(assetIndex)}>
                              <DeleteIcon />
                            </Button>
                          )}
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={6}>
                          <div className={classes.input}>
                            <Grid container alignItems="center" justifyContent="space-between">
                              <Grid item xs={9}>
                                <SearchForAsset
                                  uid={`leg-vin-${legIndex}-asset-${assetIndex}`}
                                  field={searchByFieldTypes.VIN}
                                  value={asset?.vin}
                                  clientAccountId={!showClientDropdown() ? clientAccounts[0]?.id : loadGroup?.clientAccountId}
                                  onChange={(field, val) => handleSearchFieldChange(assetIndex, field, val)}
                                  resetSearch={resetSearch}
                                  required
                                  valid={!(showMandatoryError && !asset?.vin)}
                                  assets={assets}
                                  currentIndex={assetIndex}
                                />
                              </Grid>
                              <Grid item>
                                <VinDecode
                                  vin={asset?.vin}
                                  setDecodedData={(data) => setDecodedByIndex((p) => ({ ...p, [assetIndex]: data }))}
                                  applyDecode={() => populateDecoded(assetIndex)}
                                  openDecoded={openDecoded}
                                  setOpenDecoded={(isOpen) =>
                                    setOpenDecodedByIndex((p) => ({ ...p, [assetIndex]: isOpen }))
                                  }
                                />
                              </Grid>
                            </Grid>
                          </div>
                        </Grid>

                        <Grid item xs={6}>
                          <div className={classes.input}>
                            <SearchForAsset
                              uid={`leg-asset-id-${legIndex}-asset-${assetIndex}`}
                              field={searchByFieldTypes.ASSET_ID}
                              value={asset?.assetId}
                              clientAccountId={!showClientDropdown() ? clientAccounts[0]?.id : loadGroup?.clientAccountId}
                              onChange={(field, val) => handleSearchFieldChange(assetIndex, field, val)}
                              resetSearch={resetSearch}
                              assets={assets}
                              currentIndex={assetIndex}
                            />
                          </div>
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={3}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`make-${assetIndex}`}
                              label="Vehicle Make"
                              value={asset?.make ?? ""}
                              elementType="input"
                              onChange={(val) => onAssetChange(assetIndex, "make", val)}
                              required
                              valid={!(showMandatoryError && !asset?.make)}
                              touched
                              colorInput={colorDecoded}
                            />
                            {decodedData && (
                              <VinMatching decodedData={decodedData.make} editedData={asset?.make} field="make" />
                            )}
                          </div>
                        </Grid>

                        <Grid item xs={3}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`model-${assetIndex}`}
                              label="Vehicle Model"
                              value={asset?.model ?? ""}
                              elementType="input"
                              onChange={(val) => onAssetChange(assetIndex, "model", val)}
                              required
                              valid={!(showMandatoryError && !asset?.model)}
                              touched
                              colorInput={colorDecoded}
                            />
                            {decodedData && (
                              <VinMatching decodedData={decodedData.model} editedData={asset?.model} field="model" />
                            )}
                          </div>
                        </Grid>

                        <Grid item xs={3}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`year-${assetIndex}`}
                              label="Vehicle Year"
                              value={asset?.year ?? ""}
                              elementType="input"
                              onChange={(val) => onAssetChange(assetIndex, "year", val)}
                              required
                              valid={!errors["year"] && !(showMandatoryError && !asset?.year)}
                              touched
                              numberOnly
                              colorInput={colorDecoded}
                            />
                            {decodedData && (
                              <VinMatching decodedData={decodedData.year} editedData={asset?.year} field="year" />
                            )}
                          </div>
                        </Grid>

                        <Grid item xs={3}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`weightClass-${assetIndex}`}
                              label="Vehicle Weight Class"
                              value={asset?.weightClass ?? ""}
                              elementType="dropdown"
                              values={weightClassValues}
                              onChange={(val) => onAssetChange(assetIndex, "weightClass", val)}
                              required
                              valid={!errors["weightClass"] && !(showMandatoryError && !asset?.weightClass)}
                              touched
                              colorInput={colorDecoded}
                              shrink
                              showEmpty
                            />
                            {decodedData && (
                              <VinMatching
                                decodedData={decodedData?.weightClass}
                                editedData={asset?.weightClass}
                                field="weight class"
                              />
                            )}
                          </div>
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={6}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`assetTypeId-${assetIndex}`}
                              label="Vehicle Type"
                              value={asset?.assetTypeId ?? ""}
                              elementType="dropdown"
                              onChange={(val) => onAssetChange(assetIndex, "assetTypeId", +val)}
                              values={vehicleTypes}
                              showEmpty
                              required
                              valid={!(showMandatoryError && !asset?.assetTypeId)}
                              touched
                            />
                          </div>
                        </Grid>

                        <Grid item xs={6}>
                          <div className={classes.input}>
                            <CustomInput
                              id={`fuelType-${assetIndex}`}
                              label="Vehicle Fuel Type"
                              value={asset?.fuelType ?? ""}
                              elementType="dropdown"
                              onChange={(val) => onAssetChange(assetIndex, "fuelType", +val)}
                              values={fuelTypes}
                              showEmpty
                              required
                              valid={!(showMandatoryError && !asset?.fuelType)}
                              touched
                            />
                          </div>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
                {(loadGroup?.clientAccountId || clientAccounts?.length === 1) && (
                  <Button color="primary" variant="text" onClick={addVehicle} disabled={assets?.length >= 10 ? true : false}>
                    {assets?.length >= 10 ? "Maximum 10 vehicles are allowed" : "+ Add another vehicle"}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default GroupAssetForm;
